import React, { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import './App.css';

const GRID_SIZE = 20;
const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 600;

function App() {
  const [socket, setSocket] = useState(null);
  const [gameState, setGameState] = useState('lobby');
  const [playerName, setPlayerName] = useState('');
  const [roomCode, setRoomCode] = useState('');
  const [currentRoom, setCurrentRoom] = useState(null);
  const [players, setPlayers] = useState([]);
  const [gameData, setGameData] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [serverStatus, setServerStatus] = useState('connecting'); // disconnected, connecting, connected, error
  const canvasRef = useRef(null);

  // Function to check if the server is online
  const checkServerOnline = () => {
    return serverStatus === 'connected';
  };
  
  // Setup game event listeners function to be reused
  const setupGameEventListeners = (socket) => {
    if (!socket) return;
    
    socket.on('roomCreated', (data) => {
      setCurrentRoom(data.roomCode);
      setIsHost(true);
      setGameState('waiting');
    });

    socket.on('roomJoined', (data) => {
      setCurrentRoom(data.roomCode);
      setPlayers(data.players);
      setIsHost(data.isHost);
      setGameState('waiting');
    });

    socket.on('playerJoined', (data) => {
      setPlayers(data.players);
    });

    socket.on('gameStarted', (data) => {
      setGameData(data);
      setGameState('playing');
    });

    socket.on('gameUpdate', (data) => {
      setGameData(data);
    });

    socket.on('gameOver', (data) => {
      setGameData(data);
      setGameState('gameOver');
    });

    socket.on('gamePaused', (data) => {
      setGameData(data);
      setGameState('paused');
    });

    socket.on('gameResumed', (data) => {
      setGameData(data);
      setGameState('playing');
    });

    socket.on('gameStopped', (data) => {
      setGameData(data);
      setGameState('waiting');
    });

    socket.on('error', (error) => {
      alert(error.message);
    });
  };
  
  useEffect(() => {
    // Try primary server first, fallback to original server if needed
    const connectToServer = (serverUrl) => {
      setServerStatus('connecting');
      const newSocket = io(serverUrl, {
        reconnectionAttempts: 3,
        reconnectionDelay: 1000,
        timeout: 8000
      });
      
      setSocket(newSocket);
      
      newSocket.on('connect', () => {
        console.log(`Connected to server: ${serverUrl}`);
        setServerStatus('connected');
      });
      
      newSocket.on('connect_error', (error) => {
        console.error('Connection error:', error);
        setServerStatus('error');
        
        // If primary server fails, try fallback
        if (serverUrl === 'https://server-snake-game.onrender.com') {
          console.log('Trying fallback server...');
          newSocket.close();
          setTimeout(() => {
            connectToServer('https://server-atzp.onrender.com');
          }, 1000);
        }
      });
      
      // Setup game event listeners
      setupGameEventListeners(newSocket);
      
      return newSocket;
    };
    
    const socket = connectToServer('https://server-snake-game.onrender.com');
    
    return () => socket.close();
  }, []);

  // gameeeee randerrrr
  useEffect(() => {
    if (gameState === 'playing' && gameData && canvasRef.current) {
      renderGame();
    }
  }, [gameData, gameState]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (gameState !== 'playing' || !socket) return;

      const keyMap = {
        'w': 'up',
        'W': 'up',
        'ArrowUp': 'up',
        's': 'down',
        'S': 'down',
        'ArrowDown': 'down',
        'a': 'left',
        'A': 'left',
        'ArrowLeft': 'left',
        'd': 'right',
        'D': 'right',
        'ArrowRight': 'right'
      };

      if (keyMap[e.key]) {
        socket.emit('changeDirection', keyMap[e.key]);
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameState, socket]);

  const renderGame = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !gameData) return;

    const ctx = canvas.getContext('2d');
    
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    ctx.strokeStyle = '#16213e';
    ctx.lineWidth = 1;
    for (let x = 0; x <= CANVAS_WIDTH; x += GRID_SIZE) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, CANVAS_HEIGHT);
      ctx.stroke();
    }
    for (let y = 0; y <= CANVAS_HEIGHT; y += GRID_SIZE) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(CANVAS_WIDTH, y);
      ctx.stroke();
    }

    // draw food
    gameData.food.forEach(food => {
      ctx.fillStyle = '#ffd93d';
      ctx.fillRect(
        food.x * GRID_SIZE + 2,
        food.y * GRID_SIZE + 2,
        GRID_SIZE - 4,
        GRID_SIZE - 4
      );
    });

    // draw snakiessssss
    gameData.players.forEach((player, index) => {
      if (!player.snake || player.snake.length === 0) return;

      const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dda0dd'];
      ctx.fillStyle = player.alive ? colors[index % colors.length] : '#666';

      player.snake.forEach((segment, segIndex) => {
        ctx.fillRect(
          segment.x * GRID_SIZE,
          segment.y * GRID_SIZE,
          GRID_SIZE - 2,
          GRID_SIZE - 2
        );

      
        if (segIndex === 0 && player.alive) {
          ctx.fillStyle = 'white';
          ctx.fillRect(segment.x * GRID_SIZE + 4, segment.y * GRID_SIZE + 4, 4, 4);
          ctx.fillRect(segment.x * GRID_SIZE + 12, segment.y * GRID_SIZE + 4, 4, 4);
          ctx.fillStyle = colors[index % colors.length];
        }
      });
    });

    if (gameData.gameOver) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      ctx.strokeStyle = '#32CD32'; 
      ctx.lineWidth = 4;
      ctx.strokeRect(50, CANVAS_HEIGHT/2 - 80, CANVAS_WIDTH - 100, 160);
      
      ctx.shadowColor = '#32CD32';
      ctx.shadowBlur = 15;
      
      ctx.fillStyle = '#32CD32'; 
      ctx.font = "28px 'Press Start 2P', monospace";
      ctx.textAlign = 'center';
      
      if (gameData.winner) {
        ctx.fillText(`GAME COMPLETE`, CANVAS_WIDTH/2, CANVAS_HEIGHT/2 - 30);
        ctx.font = "20px 'Press Start 2P', monospace";
        ctx.fillText(`${gameData.winner.name} WINS!`, CANVAS_WIDTH/2, CANVAS_HEIGHT/2 + 10);
      } else {
        ctx.fillText('GAME OVER', CANVAS_WIDTH/2, CANVAS_HEIGHT/2 - 20);
      }
      
      ctx.font = "12px 'Press Start 2P', monospace";
      ctx.fillText('PRESS REMATCH TO CONTINUE', CANVAS_WIDTH/2, CANVAS_HEIGHT/2 + 50);
      
      ctx.shadowBlur = 0;
    }
  }, [gameData]);

  const checkServerConnection = () => {
    if (!checkServerOnline()) {
      alert('Please wait until connected to the server before proceeding.');
      return false;
    }
    return true;
  };

  const createRoom = () => {
    if (!playerName.trim()) {
      alert('Please enter your name');
      return;
    }
    if (!checkServerConnection()) return;
    socket.emit('createRoom', { playerName: playerName.trim() });
  };

  const joinRoom = () => {
    if (!playerName.trim()) {
      alert('Please enter your name');
      return;
    }
    if (!roomCode.trim()) {
      alert('Please enter room code');
      return;
    }
    if (!checkServerConnection()) return;
    socket.emit('joinRoom', { 
      playerName: playerName.trim(), 
      roomCode: roomCode.trim().toUpperCase() 
    });
  };

  const startGame = () => {
    if (isHost) {
      socket.emit('startGame');
    }
  };

  const rematchGame = () => {
    if (isHost) {
      socket.emit('restartGame');
    }
  };

  const pauseGame = () => {
    if (isHost) {
      socket.emit('pauseGame');
    }
  };

  const resumeGame = () => {
    if (isHost) {
      socket.emit('resumeGame');
    }
  };

  const stopGame = () => {
    if (isHost) {
      socket.emit('stopGame');
    }
  };

  const backToLobby = () => {
    socket.emit('leaveRoom');
    setGameState('lobby');
    setCurrentRoom(null);
    setPlayers([]);
    setGameData(null);
    setIsHost(false);
  };

  return (
    <div className="app">
      {gameState === 'lobby' && (
        <div className="lobby">
            <h1>🐍 Multiplayer Snake</h1>
            <p className="multiplayer-notice">This game is now multiplayer-only and requires an internet connection.</p>
            
            {/* Server status indicator */}
            <div className="server-status">
              <div className={`status-indicator ${serverStatus}`}></div>
              <span>
                {serverStatus === 'connecting' && 'Connecting to server...'}
                {serverStatus === 'connected' && 'Connected to server'}
                {serverStatus === 'error' && 'Connection error - trying fallback server'}
                {serverStatus === 'disconnected' && 'Disconnected from server'}
              </span>
            {(serverStatus === 'error' || serverStatus === 'disconnected') && (
              <button 
                className="reconnect-btn" 
                onClick={() => {
                  // Close existing socket if any
                  if (socket) socket.close();
                  
                  // Extract the connectToServer function from the useEffect
                  const connectToServer = (serverUrl) => {
                    setServerStatus('connecting');
                    const newSocket = io(serverUrl, {
                      reconnectionAttempts: 3,
                      reconnectionDelay: 1000,
                      timeout: 8000
                    });
                    
                    setSocket(newSocket);
                    
                    newSocket.on('connect', () => {
                      console.log(`Connected to server: ${serverUrl}`);
                      setServerStatus('connected');
                    });
                    
                    newSocket.on('connect_error', (error) => {
                      console.error('Connection error:', error);
                      setServerStatus('error');
                      
                      // If primary server fails, try fallback
                      if (serverUrl === 'https://server-snake-game.onrender.com') {
                        console.log('Trying fallback server...');
                        newSocket.close();
                        setTimeout(() => {
                          connectToServer('https://server-atzp.onrender.com');
                        }, 1000);
                      }
                    });
                    
                    // Re-attach all game event listeners
                    setupGameEventListeners(newSocket);
                    
                    return newSocket;
                  };
                  
                  // Try to connect to the primary server
                  connectToServer('https://server-snake-game.onrender.com');
                }}
              >
                Reconnect
              </button>
            )}
          </div>
          
          <div className="form">
            <input
              type="text"
              placeholder="Enter your name"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              maxLength={15}
            />
            <input
              type="text"
              placeholder="Room code (optional)"
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
              maxLength={6}
            />
            <div className="buttons">
              <button onClick={createRoom} disabled={!checkServerOnline()}>Create Room</button>
               <button onClick={joinRoom} disabled={!checkServerOnline()}>Join Room</button>
            </div>
          </div>
        </div>
      )}

      {gameState === 'waiting' && (
        <div className="waiting">
          <h2>Room: {currentRoom}</h2>
          <p>Share this code with friends!</p>
          
          <div className="players-list">
            <h3>Players ({players.length}/4):</h3>
            {players.map((player, index) => (
              <div key={player.id} className="player-item">
                🐍 {player.name} {player.isHost && '(Host)'}
              </div>
            ))}
          </div>

          <div className="buttons">
            {isHost && (
              <button onClick={startGame} disabled={players.length < 1}>
                Start Game
              </button>
            )}
            <button onClick={backToLobby}>Leave Room</button>
          </div>
        </div>
      )}

      {(gameState === 'playing' || gameState === 'paused' || gameState === 'gameOver') && (
        <div className="game">
          <div className="game-header">
            <div className="scores">
              {gameData?.players.map((player, index) => (
                <div key={player.id} className={`score ${!player.alive ? 'dead' : ''}`}>
                  <span className="name">{player.name}</span>
                  <span className="points">{player.score}</span>
                  <span className="status">{player.alive ? '🐍' : '💀'}</span>
                </div>
              ))}
            </div>
          </div>

          {gameState === 'paused' && (
            <div className="pause-overlay">
              <h2>Game Paused</h2>
              {isHost && <p>You can resume the game when ready</p>}
              {!isHost && <p>Waiting for host to resume the game...</p>}
            </div>
          )}
          
          {gameState === 'gameOver' && (
            <div className="pause-overlay game-over-overlay">
              <div className="nokia-frame">
                <h2>GAME OVER</h2>
                {gameData?.winner && (
                  <p className="winner-text">{gameData.winner.name} WINS!</p>
                )}
                <div className="buttons">
                  {isHost && (
                    <button className="rematch-btn" onClick={rematchGame}>REMATCH</button>
                  )}
                  {!isHost && (
                    <p className="waiting-text">WAITING FOR HOST...</p>
                  )}
                  <button className="back-btn" onClick={backToLobby}>BACK TO LOBBY</button>
                </div>
              </div>
            </div>
          )}

          <canvas
            ref={canvasRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            className="game-canvas"
          />

          <div className="controls">
            <p>Use WASD or Arrow Keys to move</p>
            <div className="game-controls">
              {isHost && gameState === 'playing' && (
                <button onClick={pauseGame}>Pause Game</button>
              )}
              {isHost && gameState === 'paused' && (
                <button onClick={resumeGame}>Resume Game</button>
              )}
              {isHost && (
                <button onClick={stopGame}>Stop Game</button>
              )}
              <button onClick={backToLobby}>Leave Game</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;